import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Receipt, Plus, Trash2, Search, Eye, X, ShoppingCart, Printer, CheckCircle2, Edit, Minus, AlertTriangle, Download, FileText } from 'lucide-react';
import { formatCurrency, formatDateTimeDR, getLocalDateString } from '../utils/formatters';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const Quotations = ({ user }) => {
  const [quotations, setQuotations] = useState([]);
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [businessConfig, setBusinessConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filtros de fecha - por defecto fecha actual (zona horaria RD: UTC-4)
  const hoy = getLocalDateString();
  const [fechaDesde, setFechaDesde] = useState(hoy);
  const [fechaHasta, setFechaHasta] = useState(hoy);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerPriceList, setCustomerPriceList] = useState(null);
  const [consumidorFinalId, setConsumidorFinalId] = useState(null); // ID del Consumidor Final
  const [cart, setCart] = useState([]);
  const [formData, setFormData] = useState({
    cliente_id: '',
    cliente_nombre: '',
    cliente_rnc: '',
    cliente_telefono: '',
    cliente_direccion: '',
    vendedor: user?.full_name || '',
    fecha: getLocalDateString(),
    observaciones: '',
    validez_dias: 30, // Días de validez de la cotización (siempre 30, oculto)
    tipo_pago: 'efectivo', // Forma de pago por defecto (siempre efectivo, oculto)
  });
  
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [combinedPayments, setCombinedPayments] = useState({
    efectivo: 0,
    tarjeta: 0,
    transferencia: 0,
    cheque: 0,
  });
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productCodeInput, setProductCodeInput] = useState('');
  
  // Atajos de teclado
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!dialogOpen) return;
      
      // F2 - Abrir búsqueda de productos
      if (e.key === 'F2') {
        e.preventDefault();
        setProductSearchOpen(true);
      }
      
      // F3 - Focus en campo de código
      if (e.key === 'F3') {
        e.preventDefault();
        document.getElementById('product-code-input')?.focus();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [dialogOpen]);

  const canCreate = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'cajero';
  const canCancel = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'gerente';

  useEffect(() => {
    fetchQuotations();
    fetchProducts();
    fetchCustomers();
    fetchBusinessConfig();
  }, []);

  // Recargar cotizacións cuando cambien los filtros de fecha
  useEffect(() => {
    if (fechaDesde && fechaHasta) {
      fetchQuotations(fechaDesde, fechaHasta);
    }
  }, [fechaDesde, fechaHasta]);

  // Load CONSUMIDOR FINAL data when customers are loaded
  useEffect(() => {
    if (customers.length > 0 && !formData.cliente_nombre) {
      const consumidorFinal = customers.find(c => c.cedula === "000-0000000-0" || c.nombre.toUpperCase().includes("CONSUMIDOR FINAL"));
      if (consumidorFinal) {
        // Guardar el ID del Consumidor Final para uso posterior
        setConsumidorFinalId(consumidorFinal.id);
        
        setFormData(prev => ({
          ...prev,
          cliente_id: '', // Vacío para permitir edición
          cliente_nombre: consumidorFinal.nombre, // PRELLENADO con "CONSUMIDOR FINAL" pero editable
          cliente_rnc: consumidorFinal.rnc || '', // Prellenado con RNC por defecto
          cliente_telefono: consumidorFinal.telefono || '',
          cliente_direccion: consumidorFinal.direccion || '',
          validez_dias: 30,
        }));
        setSelectedCustomer(null); // null indica que es Consumidor Final editable
        
        // Cargar lista de precios del cliente por defecto
        loadCustomerPriceList(consumidorFinal);
      }
    }
  }, [customers]);

  const fetchBusinessConfig = async () => {
    try {
      const response = await axios.get(`${API}/business-config`);
      setBusinessConfig(response.data);
    } catch (error) {
      console.error('Error loading business config');
    }
  };

  // Removed unused fetchCotizaciónTypes function

  const fetchQuotations = async (desde = fechaDesde, hasta = fechaHasta) => {
    try {
      // Construir URL con filtros de fecha
      const params = new URLSearchParams();
      
      // Convertir fechas de YYYY-MM-DD a DD/MM/YYYY (formato esperado por el backend)
      if (desde) {
        const [year, month, day] = desde.split('-');
        const fechaFormateada = `${day}/${month}/${year}`;
        params.append('fecha_desde', fechaFormateada);
      }
      if (hasta) {
        const [year, month, day] = hasta.split('-');
        const fechaFormateada = `${day}/${month}/${year}`;
        params.append('fecha_hasta', fechaFormateada);
      }
      
      const response = await axios.get(`${API}/quotations?${params.toString()}`);
      setQuotations(response.data);
    } catch (error) {
      toast.error('Error al cargar cotizaciones');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data.filter(p => p.existencia > 0));
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/clients`);
      setCustomers(response.data.filter(c => c.is_active));
    } catch (error) {
      console.error('Error al cargar clientes');
    }
  };

  const searchCustomerByIdentifier = (searchValue) => {
    if (!searchValue) return null;
    
    const search = searchValue.trim().toLowerCase();
    return customers.find(c => 
      c.rnc?.toLowerCase().includes(search) ||
      c.cedula?.toLowerCase().includes(search) ||
      c.telefono?.includes(search) ||
      c.nombre?.toLowerCase().includes(search)
    );
  };

  // Obtener código de cliente por nombre
  const getClientCode = (clientName) => {
    const client = customers.find(c => 
      c.nombre?.toLowerCase() === clientName?.toLowerCase()
    );
    return client?.codigo_cliente || '-';
  };

  const handleCustomerSearch = (e) => {
    const value = e.target.value;
    setCustomerSearchTerm(value);
    
    // Búsqueda automática después de 3 caracteres
    if (value.length >= 3) {
      const found = searchCustomerByIdentifier(value);
      if (found) {
        selectCustomer(found);
      }
    }
  };

  const handleCustomerSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Abrir modal de búsqueda avanzada con el término prerellenado
      setCustomerSearchOpen(true);
      toast.info('Abriendo búsqueda avanzada de clientes...');
    }
  };

  // Función para cargar lista de precios de un cliente
  const loadCustomerPriceList = async (customer) => {
    if (customer.lista_precios_id) {
      try {
        const response = await axios.get(`${API}/price-lists/${customer.lista_precios_id}`);
        setCustomerPriceList(response.data);
        return response.data;
      } catch (error) {
        console.error('Error al cargar lista de precios:', error);
        setCustomerPriceList(null);
        return null;
      }
    } else {
      // Si no tiene lista asignada, buscar la lista General
      try {
        const response = await axios.get(`${API}/price-lists`);
        const generalList = response.data.find(list => list.es_general);
        if (generalList) {
          setCustomerPriceList(generalList);
          return generalList;
        }
      } catch (error) {
        console.error('Error al cargar lista General:', error);
      }
      setCustomerPriceList(null);
      return null;
    }
  };

  const selectCustomer = async (customer) => {
    setFormData(prev => ({
      ...prev,
      cliente_id: customer.id,
      cliente_nombre: customer.nombre,
      cliente_rnc: customer.rnc || '',
      cliente_telefono: customer.telefono || '',
      cliente_direccion: customer.direccion || '',
      validez_dias: 30,
    }));
    setSelectedCustomer(customer);
    setCustomerSearchOpen(false);
    setCustomerSearchTerm('');
    
    // Cargar lista de precios del cliente
    const priceList = await loadCustomerPriceList(customer);
    
    // Recalcular precios del carrito si hay productos
    if (cart.length > 0) {
      await recalculateCartPrices(customer.id);
    }
    
    // Mostrar mensaje apropiado
    if (priceList) {
      let message = `Cliente seleccionado: ${customer.nombre} (Lista: ${priceList.nombre})`;
      if (priceList.descuento_global > 0) {
        message += ` -${priceList.descuento_global}%`;
      }
      toast.success(message);
    } else {
      toast.success(`Cliente seleccionado: ${customer.nombre}`);
    }
  };

  const handleProductCodeSubmit = (e) => {
    e.preventDefault();
    if (!productCodeInput.trim()) return;
    
    // Buscar por código interno o código de barras
    const product = products.find(p => 
      p.codigo_interno === productCodeInput.trim() || 
      (Array.isArray(p.codigo_barras) ? p.codigo_barras.includes(productCodeInput.trim()) : p.codigo_barras === productCodeInput.trim())
    );
    
    if (product) {
      // Producto encontrado: flujo normal
      addToCart(product);
      setProductCodeInput('');
      toast.success(`${product.nombre} agregado`);
    } else {
      // Producto NO encontrado: abrir modal de búsqueda avanzada con el texto prerellenado
      setProductSearchTerm(productCodeInput.trim()); // Prerellenar búsqueda
      setProductSearchOpen(true); // Abrir modal
      setProductCodeInput(''); // Limpiar campo
      toast.info('Producto no encontrado. Abriendo búsqueda avanzada...');
    }
  };

  const handleCombinedPaymentSave = () => {
    const total = Object.values(combinedPayments).reduce((a, b) => a + parseFloat(b || 0), 0);
    const invoiceTotal = calculateTotals().total;
    
    if (Math.abs(total - invoiceTotal) > 0.01) {
      toast.error(`El total del pago combinado (${formatCurrency(total)}) no coincide con el total de la cotización (${formatCurrency(invoiceTotal)})`);
      return;
    }
    
    setFormData(prev => ({ ...prev, tipo_pago: 'combinado' }));
    setPaymentModalOpen(false);
    toast.success('Pago combinado configurado');
  };

  const filteredProductsForSearch = products.filter(p =>
    p.nombre.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
    p.codigo_interno?.includes(productSearchTerm) ||
    (Array.isArray(p.codigo_barras) ? p.codigo_barras.some(b => b.includes(productSearchTerm)) : p.codigo_barras?.includes(productSearchTerm)) ||
    p.categoria?.toLowerCase().includes(productSearchTerm.toLowerCase())
  );

  const handlePrintQuotation = (invoice) => {
    // Función auxiliar para formatear fecha en impresión
    const formatPrintDate = (dateStr) => {
      if (!dateStr) return '-';
      
      // Si ya tiene el formato DD/MM/YYYY, extraer solo la fecha
      if (typeof dateStr === 'string' && dateStr.includes('/')) {
        return dateStr.split(' ')[0];
      }
      
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '-';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    };
    
    const formatPrintTime = (dateStr) => {
      if (!dateStr) return '-';
      
      // Si ya tiene el formato con hora AM/PM
      if (typeof dateStr === 'string' && dateStr.includes('/')) {
        const parts = dateStr.split(' ');
        if (parts.length >= 3) {
          const timePart = parts[1];
          const ampmPart = parts[2];
          const timeOnly = timePart.split(':').slice(0, 2).join(':');
          const ampmFormatted = ampmPart.toLowerCase() === 'pm' ? 'p. m.' : 'a. m.';
          return `${timeOnly} ${ampmFormatted}`;
        }
      }
      
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '-';
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'p. m.' : 'a. m.';
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
    };
    
    // Usar valores de businessConfig o fallbacks
    const config = {
      logo: businessConfig?.logo || '',
      nombre_comercio: businessConfig?.nombre_comercio || businessConfig?.sistema_nombre || 'LICORSTORE RD',
      sistema_nombre: businessConfig?.sistema_nombre || 'Sistema de Gestión',
      rnc: businessConfig?.rnc || '',
      telefono: businessConfig?.telefono || '',
      direccion: businessConfig?.direccion || '',
      slogan: businessConfig?.slogan || ''
    };
    
    const printWindow = window.open('', '', 'width=280,height=600');
    
    // Formato optimizado para impresoras térmicas 72mm - IGUAL QUE FACTURAS
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Cotización ${invoice.numero_cotizacion}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            @page {
              size: 72mm auto;
              margin: 2mm;
            }
            
            @media print {
              body { 
                margin: 0 !important; 
                padding: 0 !important;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
            
            body { 
              font-family: 'Arial', 'Helvetica', sans-serif; 
              font-size: 12px;
              line-height: 1.4;
              width: 68mm;
              margin: 0 auto;
              padding: 3mm;
              color: #000;
              background: #fff;
            }
            
            .header { 
              text-align: center; 
              border-bottom: 2px dashed #000; 
              padding-bottom: 3mm; 
              margin-bottom: 3mm; 
            }
            
            .logo { 
              max-width: 45mm; 
              max-height: 18mm; 
              margin: 0 auto 2mm; 
              display: block; 
            }
            
            h2 { 
              margin: 2mm 0; 
              font-size: 16px; 
              font-weight: bold;
              letter-spacing: 0.5px;
            }
            
            p { 
              margin: 1mm 0; 
              font-size: 11px;
              line-height: 1.3;
            }
            
            .cotizacion-badge {
              font-size: 14px;
              font-weight: bold;
              margin: 3mm 0 1mm;
              padding: 2mm 4mm;
              border: 2px solid #000;
              display: inline-block;
              letter-spacing: 1px;
            }
            
            .info { 
              margin-bottom: 3mm; 
              font-size: 11px;
              line-height: 1.4;
            }
            
            .info-row { 
              display: flex; 
              justify-content: space-between; 
              margin: 1mm 0;
              font-size: 11px;
              line-height: 1.4;
            }
            
            .separator { 
              border-top: 1px dashed #000; 
              margin: 3mm 0; 
            }
            
            .items { 
              margin: 3mm 0; 
            }
            
            .item-row { 
              margin: 2mm 0; 
            }
            
            .item-detail { 
              display: flex; 
              justify-content: space-between; 
              font-size: 11px;
              line-height: 1.4;
            }
            
            .totals { 
              margin-top: 3mm; 
              border-top: 2px solid #000; 
              padding-top: 3mm; 
            }
            
            .total-row { 
              display: flex; 
              justify-content: space-between; 
              margin: 1.5mm 0; 
              font-size: 12px;
              line-height: 1.4;
            }
            
            .grand-total { 
              font-weight: bold; 
              font-size: 15px; 
              border-top: 2px solid #000; 
              padding-top: 3mm; 
              margin-top: 2mm; 
            }
            
            .footer { 
              text-align: center; 
              margin-top: 4mm; 
              font-size: 10px; 
              border-top: 1px dashed #000; 
              padding-top: 3mm; 
              line-height: 1.4;
            }
            
            .bold { 
              font-weight: bold; 
            }

            .small { 
              font-size: 10px; 
            }

            .product-name {
              font-size: 11px;
              font-weight: bold;
              margin-bottom: 1mm;
              line-height: 1.3;
            }
            
            .validity {
              font-size: 11px;
              font-weight: bold;
              margin: 2mm 0;
              padding: 2mm;
              border: 1px dashed #000;
            }
            
            .not-invoice {
              font-size: 12px;
              font-weight: bold;
              margin-top: 3mm;
              padding: 2mm;
              border: 2px solid #000;
            }
          </style>
        </head>
        <body>
          <div class="header">
            ${config.logo ? `<img src="${config.logo}" alt="Logo" class="logo" />` : ''}
            <h2>${config.nombre_comercio || config.sistema_nombre}</h2>
            ${config.slogan ? `<p class="small">${config.slogan}</p>` : ''}
            ${config.rnc ? `<p>RNC: ${config.rnc}</p>` : ''}
            ${config.telefono ? `<p>Tel: ${config.telefono}</p>` : ''}
            ${config.direccion ? `<p class="small">${config.direccion}</p>` : ''}
            <div class="cotizacion-badge">*** COTIZACIÓN ***</div>
          </div>
          
          <div class="info">
            <div class="info-row"><span>No:</span><span class="bold">${invoice.numero_cotizacion}</span></div>
            <div class="info-row"><span>Fecha:</span><span>${formatPrintDate(invoice.fecha)}</span></div>
            <div class="info-row"><span>Hora:</span><span>${formatPrintTime(invoice.fecha)}</span></div>
            <div class="info-row"><span>Vendedor:</span><span>${invoice.vendedor_nombre || '-'}</span></div>
          </div>

          <div class="separator"></div>
          
          <div class="info">
            <div class="bold" style="margin-bottom: 1mm; font-size: 12px;">CLIENTE:</div>
            <div style="font-size: 11px;">${invoice.cliente?.nombre || invoice.cliente_nombre || 'CONSUMIDOR FINAL'}</div>
            ${(invoice.cliente?.rnc || invoice.cliente_rnc) && (invoice.cliente?.rnc || invoice.cliente_rnc) !== '000000000' && (invoice.cliente?.rnc || invoice.cliente_rnc) !== '000-0000000-0' ? 
              `<div class="small">RNC: ${invoice.cliente?.rnc || invoice.cliente_rnc}</div>` : ''}
            ${(invoice.cliente?.telefono || invoice.cliente_telefono) ? `<div class="small">Tel: ${invoice.cliente?.telefono || invoice.cliente_telefono}</div>` : ''}
          </div>

          <div class="separator"></div>

          <div class="items">
            <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 11px; margin-bottom: 2mm; border-bottom: 2px solid #000; padding-bottom: 1mm;">
              <span style="flex: 3;">Producto</span>
              <span style="flex: 1; text-align: center;">Cant</span>
              <span style="flex: 1.5; text-align: right;">Total</span>
            </div>
            ${(invoice.items || []).map(item => {
              return `
              <div class="item-row">
                <div class="product-name">${item.producto_nombre || item.nombre}</div>
                <div class="item-detail">
                  <span style="flex: 2;">${item.cantidad} x RD$ ${(item.precio_unitario || 0).toFixed(2)}</span>
                  <span style="flex: 1.5; text-align: right; font-weight: bold;">RD$ ${(item.subtotal || 0).toFixed(2)}</span>
                </div>
              </div>
              `;
            }).join('')}
          </div>

          <div class="totals">
            <div class="total-row">
              <span>Subtotal:</span>
              <span class="bold">RD$ ${(invoice.subtotal || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
            </div>
            ${invoice.itbis > 0 ? `
              <div class="total-row">
                <span>ITBIS (18%):</span>
                <span class="bold">RD$ ${(invoice.itbis || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
            ` : ''}
            <div class="total-row grand-total">
              <span>TOTAL:</span>
              <span>RD$ ${(invoice.total || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
            </div>
          </div>

          <div class="footer">
            <div class="validity">
              Validez: ${invoice.dias_validez || 30} días
            </div>
            <p class="bold" style="font-size: 12px; margin: 2mm 0;">¡Gracias por su preferencia!</p>
            <p style="font-size: 11px;">${config.nombre_comercio || config.sistema_nombre}</p>
            <div class="not-invoice">*** NO ES FACTURA ***</div>
          </div>

          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() {
                  window.close();
                }, 100);
              }, 250);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    
    toast.success('Imprimiendo cotización...');
  };

  // Función para imprimir cotización en formato A4
  const printQuotationA4 = (quotation) => {
    const config = businessConfig || { nombre_comercio: 'LICORSTORE RD', rnc: '', telefono: '', direccion: '' };
    const itbisRate = businessConfig?.itbis_habilitado ? (businessConfig.itbis_porcentaje || 18) / 100 : 0;
    const printWindow = window.open('', '', 'width=900,height=700');
    
    // Función para formatear fecha/hora
    const formatPrintDateTime = (dateStr) => {
      if (!dateStr) return '-';
      
      if (typeof dateStr === 'string' && dateStr.includes('/')) {
        const parts = dateStr.split(' ');
        if (parts.length >= 3) {
          const datePart = parts[0];
          const timePart = parts[1];
          const ampmPart = parts[2];
          const timeOnly = timePart.split(':').slice(0, 2).join(':');
          const ampmFormatted = ampmPart.toLowerCase() === 'pm' ? 'p.m.' : 'a.m.';
          return `${datePart} ${timeOnly} ${ampmFormatted}`;
        }
        return dateStr;
      }
      
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr || '-';
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'p.m.' : 'a.m.';
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${day}/${month}/${year} ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
    };
    
    const fechaFormateada = formatPrintDateTime(quotation.fecha);
    
    const formatMoney = (value) => {
      return (value || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2});
    };
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Cotización ${quotation.numero_cotizacion}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            @page { size: A4; margin: 15mm; }
            @media print { body { margin: 0 !important; } }
            body { 
              font-family: 'Helvetica Neue', Arial, sans-serif; 
              font-size: 10px;
              line-height: 1.4;
              color: #333;
              background: #fff;
              padding: 20mm;
            }
            
            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              padding-bottom: 20px;
              border-bottom: 2px solid #000;
              margin-bottom: 20px;
            }
            .company { max-width: 60%; }
            .company-name {
              font-size: 22px;
              font-weight: 700;
              letter-spacing: -0.5px;
              margin-bottom: 8px;
            }
            .company-info {
              font-size: 9px;
              color: #666;
              line-height: 1.6;
            }
            .quote-header { text-align: right; }
            .quote-title {
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: 2px;
              color: #666;
              margin-bottom: 5px;
            }
            .quote-number {
              font-size: 20px;
              font-weight: 700;
              margin-bottom: 10px;
            }
            
            .details-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 30px;
              margin-bottom: 25px;
            }
            .detail-section h3 {
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              color: #999;
              margin-bottom: 10px;
              padding-bottom: 5px;
              border-bottom: 1px solid #eee;
            }
            .detail-row {
              display: flex;
              margin-bottom: 6px;
              font-size: 10px;
            }
            .detail-label {
              width: 80px;
              color: #666;
            }
            .detail-value {
              flex: 1;
              font-weight: 500;
            }
            
            .table-section { margin-bottom: 25px; }
            .table-title {
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              color: #999;
              margin-bottom: 10px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            thead th {
              text-align: left;
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #666;
              padding: 10px 8px;
              border-bottom: 2px solid #333;
              font-weight: 600;
            }
            thead th.right { text-align: right; }
            thead th.center { text-align: center; }
            tbody td {
              padding: 10px 8px;
              border-bottom: 1px solid #eee;
              font-size: 10px;
            }
            tbody td.right { 
              text-align: right; 
              font-family: 'Courier New', monospace;
            }
            tbody td.center { text-align: center; }
            tbody tr:last-child td { border-bottom: 2px solid #333; }
            .item-count {
              font-size: 9px;
              color: #666;
              margin-top: 10px;
              text-align: right;
            }
            
            .totals-section {
              display: flex;
              justify-content: flex-end;
              margin-bottom: 30px;
            }
            .totals-box { width: 250px; }
            .totals-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              font-size: 10px;
            }
            .totals-row.subtotal { border-bottom: 1px solid #eee; }
            .totals-row.total {
              border-top: 2px solid #333;
              margin-top: 5px;
              padding-top: 12px;
            }
            .totals-row.total .label,
            .totals-row.total .value {
              font-size: 14px;
              font-weight: 700;
            }
            .totals-row .label { color: #666; }
            .totals-row .value {
              font-family: 'Courier New', monospace;
              font-weight: 500;
            }
            
            .footer {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 40px;
              padding-top: 20px;
              border-top: 1px solid #eee;
            }
            .footer-section h4 {
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              color: #999;
              margin-bottom: 15px;
            }
            .signature-area {
              height: 60px;
              border-bottom: 1px solid #333;
            }
            .signature-label {
              font-size: 8px;
              color: #999;
              text-align: center;
              margin-top: 5px;
            }
            .footer-info {
              font-size: 9px;
              color: #666;
              line-height: 1.6;
            }
            
            .not-invoice-notice {
              text-align: center;
              margin-top: 20px;
              padding: 10px;
              border: 2px dashed #f59e0b;
              background: #fffbeb;
              color: #92400e;
              font-size: 11px;
              font-weight: 600;
            }
            
            .observations {
              margin-top: 15px;
              padding: 10px;
              background: #f9f9f9;
              font-size: 9px;
            }
            .obs-label {
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #666;
              margin-bottom: 5px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company">
              <div class="company-name">${config.nombre_comercio || 'LICORSTORE RD'}</div>
              <div class="company-info">
                ${config.rnc ? `RNC: ${config.rnc}<br>` : ''}
                ${config.direccion ? `${config.direccion}<br>` : ''}
                ${config.telefono ? `Tel: ${config.telefono}` : ''}
              </div>
            </div>
            <div class="quote-header">
              <div class="quote-title">Cotización</div>
              <div class="quote-number">${quotation.numero_cotizacion}</div>
            </div>
          </div>
          
          <div class="details-grid">
            <div class="detail-section">
              <h3>Cliente</h3>
              <div class="detail-row">
                <span class="detail-label">Nombre</span>
                <span class="detail-value">${quotation.cliente?.nombre || quotation.cliente_nombre || 'CONSUMIDOR FINAL'}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">RNC/Cédula</span>
                <span class="detail-value">${quotation.cliente?.rnc || quotation.cliente_rnc || '—'}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Teléfono</span>
                <span class="detail-value">${quotation.cliente?.telefono || quotation.cliente_telefono || '—'}</span>
              </div>
            </div>
            <div class="detail-section">
              <h3>Cotización</h3>
              <div class="detail-row">
                <span class="detail-label">Fecha</span>
                <span class="detail-value">${fechaFormateada}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Estado</span>
                <span class="detail-value">${quotation.status || 'Pendiente'}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Vendedor</span>
                <span class="detail-value">${quotation.vendedor_nombre || 'Admin'}</span>
              </div>
            </div>
          </div>
          
          <div class="table-section">
            <div class="table-title">Detalle de Productos</div>
            <table>
              <thead>
                <tr>
                  <th style="width:30px">#</th>
                  <th>Descripción</th>
                  <th class="right" style="width:80px">Precio</th>
                  <th class="center" style="width:50px">Cant.</th>
                  <th class="right" style="width:80px">Subtotal</th>
                  <th class="right" style="width:70px">ITBIS</th>
                  <th class="right" style="width:90px">Total</th>
                </tr>
              </thead>
              <tbody>
                ${(quotation.items || []).map((item, index) => {
                  const itemTotalConItbis = (item.precio_unitario || 0) * item.cantidad;
                  const itemSubtotal = itbisRate > 0 ? Math.round((itemTotalConItbis / (1 + itbisRate)) * 100) / 100 : itemTotalConItbis;
                  const itemItbis = Math.round((itemTotalConItbis - itemSubtotal) * 100) / 100;
                  const itemTotal = itemTotalConItbis;
                  return `
                    <tr>
                      <td class="center">${index + 1}</td>
                      <td>${item.producto_nombre || item.nombre}</td>
                      <td class="right">${formatMoney(item.precio_unitario)}</td>
                      <td class="center">${item.cantidad}</td>
                      <td class="right">${formatMoney(itemSubtotal)}</td>
                      <td class="right">${formatMoney(itemItbis || 0)}</td>
                      <td class="right">${formatMoney(itemTotal)}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
            <div class="item-count">${(quotation.items || []).length} artículo(s)</div>
          </div>
          
          <div class="totals-section">
            <div class="totals-box">
              <div class="totals-row subtotal">
                <span class="label">Subtotal</span>
                <span class="value">RD$ ${formatMoney(quotation.subtotal)}</span>
              </div>
              <div class="totals-row">
                <span class="label">ITBIS (18%)</span>
                <span class="value">RD$ ${formatMoney(quotation.itbis || 0)}</span>
              </div>
              <div class="totals-row total">
                <span class="label">Total</span>
                <span class="value">RD$ ${formatMoney(quotation.total)}</span>
              </div>
            </div>
          </div>
          
          ${quotation.observaciones ? `
          <div class="observations">
            <div class="obs-label">Observaciones</div>
            ${quotation.observaciones}
          </div>
          ` : ''}
          
          <div class="footer">
            <div class="footer-section">
              <h4>Elaborado por</h4>
              <div class="footer-info">
                ${quotation.vendedor_nombre || 'Administrador'}<br>
                ${config.nombre_comercio || ''}
              </div>
            </div>
            <div class="footer-section">
              <h4>Aceptado por</h4>
              <div class="signature-area"></div>
              <div class="signature-label">Firma del Cliente</div>
            </div>
          </div>
          
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() { window.close(); }, 100);
              }, 300);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Función para descargar cotización como PDF
  const downloadQuotationPDF = async (quotation) => {
    const config = businessConfig || { nombre_comercio: 'LICORSTORE RD', rnc: '', telefono: '', direccion: '' };
    const itbisRate = businessConfig?.itbis_habilitado ? (businessConfig.itbis_porcentaje || 18) / 100 : 0;
    
    const formatPrintDateTime = (dateStr) => {
      if (!dateStr) return '-';
      
      if (typeof dateStr === 'string' && dateStr.includes('/')) {
        const parts = dateStr.split(' ');
        if (parts.length >= 3) {
          const datePart = parts[0];
          const timePart = parts[1];
          const ampmPart = parts[2];
          const timeOnly = timePart.split(':').slice(0, 2).join(':');
          const ampmFormatted = ampmPart.toLowerCase() === 'pm' ? 'p.m.' : 'a.m.';
          return `${datePart} ${timeOnly} ${ampmFormatted}`;
        }
        return dateStr;
      }
      
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr || '-';
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'p.m.' : 'a.m.';
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${day}/${month}/${year} ${String(hours).padStart(2, '0')}:${minutes} ${ampm}`;
    };
    
    const fechaFormateada = formatPrintDateTime(quotation.fecha);
    
    const formatMoney = (value) => {
      return (value || 0).toLocaleString('es-DO', {minimumFractionDigits: 2, maximumFractionDigits: 2});
    };
    
    toast.info('Generando PDF...');
    
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position: fixed; top: 0; left: 0; width: 210mm; height: 297mm; border: none; visibility: hidden;';
    document.body.appendChild(iframe);
    
    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    
    iframeDoc.open();
    iframeDoc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: 'Helvetica Neue', Arial, sans-serif; 
              font-size: 10px;
              line-height: 1.4;
              color: #333;
              background: #fff;
              padding: 15mm;
              width: 210mm;
              min-height: 297mm;
            }
            
            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              padding-bottom: 20px;
              border-bottom: 2px solid #000;
              margin-bottom: 20px;
            }
            .company { max-width: 60%; }
            .company-name {
              font-size: 22px;
              font-weight: 700;
              letter-spacing: -0.5px;
              margin-bottom: 8px;
            }
            .company-info {
              font-size: 9px;
              color: #666;
              line-height: 1.6;
            }
            .quote-header { text-align: right; }
            .quote-title {
              font-size: 11px;
              text-transform: uppercase;
              letter-spacing: 2px;
              color: #666;
              margin-bottom: 5px;
            }
            .quote-number {
              font-size: 20px;
              font-weight: 700;
              margin-bottom: 10px;
            }
            
            .details-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 30px;
              margin-bottom: 25px;
            }
            .detail-section h3 {
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              color: #999;
              margin-bottom: 10px;
              padding-bottom: 5px;
              border-bottom: 1px solid #eee;
            }
            .detail-row {
              display: flex;
              margin-bottom: 6px;
              font-size: 10px;
            }
            .detail-label { width: 80px; color: #666; }
            .detail-value { flex: 1; font-weight: 500; }
            
            .validity-box {
              background: #fef3c7;
              padding: 10px 15px;
              margin-top: 10px;
              border-left: 3px solid #f59e0b;
            }
            .validity-label {
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #92400e;
            }
            .validity-value {
              font-size: 12px;
              font-weight: 600;
              color: #92400e;
            }
            
            .table-section { margin-bottom: 25px; }
            .table-title {
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              color: #999;
              margin-bottom: 10px;
            }
            table { width: 100%; border-collapse: collapse; }
            thead th {
              text-align: left;
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1px;
              color: #666;
              padding: 10px 8px;
              border-bottom: 2px solid #333;
              font-weight: 600;
            }
            thead th.right { text-align: right; }
            thead th.center { text-align: center; }
            tbody td {
              padding: 10px 8px;
              border-bottom: 1px solid #eee;
              font-size: 10px;
            }
            tbody td.right { text-align: right; font-family: 'Courier New', monospace; }
            tbody td.center { text-align: center; }
            tbody tr:last-child td { border-bottom: 2px solid #333; }
            .item-count {
              font-size: 9px;
              color: #666;
              margin-top: 10px;
              text-align: right;
            }
            
            .totals-section {
              display: flex;
              justify-content: flex-end;
              margin-bottom: 30px;
            }
            .totals-box { width: 250px; }
            .totals-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              font-size: 10px;
            }
            .totals-row.subtotal { border-bottom: 1px solid #eee; }
            .totals-row.total {
              border-top: 2px solid #333;
              margin-top: 5px;
              padding-top: 12px;
            }
            .totals-row.total .label,
            .totals-row.total .value {
              font-size: 14px;
              font-weight: 700;
            }
            .totals-row .label { color: #666; }
            .totals-row .value { font-family: 'Courier New', monospace; font-weight: 500; }
            
            .footer {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 40px;
              padding-top: 20px;
              border-top: 1px solid #eee;
            }
            .footer-section h4 {
              font-size: 8px;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              color: #999;
              margin-bottom: 15px;
            }
            .signature-area { height: 60px; border-bottom: 1px solid #333; }
            .signature-label { font-size: 8px; color: #999; text-align: center; margin-top: 5px; }
            .footer-info { font-size: 9px; color: #666; line-height: 1.6; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company">
              <div class="company-name">${config.nombre_comercio || 'LICORSTORE RD'}</div>
              <div class="company-info">
                ${config.rnc ? `RNC: ${config.rnc}<br>` : ''}
                ${config.direccion ? `${config.direccion}<br>` : ''}
                ${config.telefono ? `Tel: ${config.telefono}` : ''}
              </div>
            </div>
            <div class="quote-header">
              <div class="quote-title">Cotización</div>
              <div class="quote-number">${quotation.numero_cotizacion}</div>
            </div>
          </div>
          
          <div class="details-grid">
            <div class="detail-section">
              <h3>Cliente</h3>
              <div class="detail-row">
                <span class="detail-label">Nombre</span>
                <span class="detail-value">${quotation.cliente?.nombre || quotation.cliente_nombre || 'CONSUMIDOR FINAL'}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">RNC/Cédula</span>
                <span class="detail-value">${quotation.cliente?.rnc || quotation.cliente_rnc || '—'}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Teléfono</span>
                <span class="detail-value">${quotation.cliente?.telefono || quotation.cliente_telefono || '—'}</span>
              </div>
            </div>
            <div class="detail-section">
              <h3>Cotización</h3>
              <div class="detail-row">
                <span class="detail-label">Fecha</span>
                <span class="detail-value">${fechaFormateada}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Estado</span>
                <span class="detail-value">${quotation.status || 'Pendiente'}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Vendedor</span>
                <span class="detail-value">${quotation.vendedor_nombre || 'Admin'}</span>
              </div>
            </div>
          </div>
          
          <div class="table-section">
            <div class="table-title">Detalle de Productos</div>
            <table>
              <thead>
                <tr>
                  <th style="width:30px">#</th>
                  <th>Descripción</th>
                  <th class="right" style="width:80px">Precio</th>
                  <th class="center" style="width:50px">Cant.</th>
                  <th class="right" style="width:80px">Subtotal</th>
                  <th class="right" style="width:70px">ITBIS</th>
                  <th class="right" style="width:90px">Total</th>
                </tr>
              </thead>
              <tbody>
                ${(quotation.items || []).map((item, index) => {
                  const itemTotalConItbis = (item.precio_unitario || 0) * item.cantidad;
                  const itemSubtotal = itbisRate > 0 ? Math.round((itemTotalConItbis / (1 + itbisRate)) * 100) / 100 : itemTotalConItbis;
                  const itemItbis = Math.round((itemTotalConItbis - itemSubtotal) * 100) / 100;
                  const itemTotal = itemTotalConItbis;
                  return `
                    <tr>
                      <td class="center">${index + 1}</td>
                      <td>${item.producto_nombre || item.nombre}</td>
                      <td class="right">${formatMoney(item.precio_unitario)}</td>
                      <td class="center">${item.cantidad}</td>
                      <td class="right">${formatMoney(itemSubtotal)}</td>
                      <td class="right">${formatMoney(itemItbis || 0)}</td>
                      <td class="right">${formatMoney(itemTotal)}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
            <div class="item-count">${(quotation.items || []).length} artículo(s)</div>
          </div>
          
          <div class="totals-section">
            <div class="totals-box">
              <div class="totals-row subtotal">
                <span class="label">Subtotal</span>
                <span class="value">RD$ ${formatMoney(quotation.subtotal)}</span>
              </div>
              <div class="totals-row">
                <span class="label">ITBIS (18%)</span>
                <span class="value">RD$ ${formatMoney(quotation.itbis || 0)}</span>
              </div>
              <div class="totals-row total">
                <span class="label">Total</span>
                <span class="value">RD$ ${formatMoney(quotation.total)}</span>
              </div>
            </div>
          </div>
          
          <div class="footer">
            <div class="footer-section">
              <h4>Elaborado por</h4>
              <div class="footer-info">
                ${quotation.vendedor_nombre || 'Administrador'}<br>
                ${config.nombre_comercio || ''}
              </div>
            </div>
            <div class="footer-section">
              <h4>Aceptado por</h4>
              <div class="signature-area"></div>
              <div class="signature-label">Firma del Cliente</div>
            </div>
          </div>
        </body>
      </html>
    `);
    iframeDoc.close();
    
    // Esperar a que cargue el contenido
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      const canvas = await html2canvas(iframeDoc.body, {
        scale: 2,
        useCORS: true,
        logging: false,
        width: 794, // A4 width in pixels at 96 DPI
        height: 1123, // A4 height in pixels at 96 DPI
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`Cotizacion_${quotation.numero_cotizacion}.pdf`);
      
      toast.success('PDF descargado correctamente');
    } catch (error) {
      console.error('Error generando PDF:', error);
      toast.error('Error al generar PDF. Intente imprimir en A4.');
    } finally {
      document.body.removeChild(iframe);
    }
  };

  const handleCustomerSelect = (customerId) => {
    if (customerId === "new") {
      // Load CONSUMIDOR FINAL data - SIEMPRE EDITABLE con campos prellenados
      const consumidorFinal = customers.find(c => c.cedula === "000-0000000-0");
      setSelectedCustomer(null); // null = modo editable
      
      if (consumidorFinal) {
        setConsumidorFinalId(consumidorFinal.id); // Guardar ID para usar al guardar
        setFormData({
          ...formData,
          cliente_id: '', // Vacío para permitir edición de campos
          cliente_nombre: consumidorFinal.nombre, // PRELLENADO con "CONSUMIDOR FINAL" pero editable
          cliente_rnc: consumidorFinal.rnc || '', // Prellenado con RNC por defecto
          cliente_telefono: consumidorFinal.telefono || '',
          cliente_direccion: consumidorFinal.direccion || '',
        });
        // Cargar lista de precios General
        loadCustomerPriceList(consumidorFinal);
      } else {
        // Fallback if CONSUMIDOR FINAL not found
        setFormData({
          ...formData,
          cliente_id: '',
          cliente_nombre: 'CONSUMIDOR FINAL', // Prellenado
          cliente_rnc: '000000000', // Prellenado
          cliente_telefono: '000-000-0000',
          cliente_direccion: 'SIN DIRECCION',
        });
        setCustomerPriceList(null); // Lista General
      }
    } else {
      // Cliente registrado seleccionado
      const customer = customers.find(c => c.id === customerId);
      if (customer) {
        setSelectedCustomer(customer);
        setFormData({
          ...formData,
          cliente_id: customer.id, // ID real del cliente
          cliente_nombre: customer.nombre,
          cliente_rnc: customer.rnc || '',
          cliente_telefono: customer.telefono || '',
          cliente_direccion: customer.direccion || '',
        });
        // Cargar lista de precios del cliente
        loadCustomerPriceList(customer);
      }
    }
  };


  const recalculateCartPrices = async (clienteId) => {
    if (cart.length === 0) return;
    
    try {
      const updatedCart = [];
      for (const item of cart) {
        // Obtener nuevo precio según cliente
        const response = await axios.get(
          `${API}/products/${item.producto_id}/price?cliente_id=${clienteId || ''}`
        );
        
        updatedCart.push({
          ...item,
          precio_unitario: response.data.precio_final,
          subtotal: item.cantidad * response.data.precio_final
        });
      }
      
      setCart(updatedCart);
      toast.success('Precios actualizados según lista del cliente');
    } catch (error) {
      console.error('Error al recalcular precios:', error);
      toast.error('Error al actualizar precios');
    }
  };

  const togglePriceType = (productId) => {
    // Solo permitir cambio de precio si estamos usando Lista General
    const isGeneralList = !customerPriceList || customerPriceList.es_general;
    
    if (!isGeneralList) {
      toast.error('Solo se puede cambiar el precio en la Lista General');
      return;
    }

    setCart(cart.map(item => {
      if (item.producto_id === productId) {
        const product = products.find(p => p.id === productId);
        
        if (!product || !product.precio_minimo) {
          toast.error('Este producto no tiene precio mínimo configurado');
          return item;
        }

        // Alternar entre precio normal y precio mínimo
        const isUsingMinPrice = item.precio_unitario === product.precio_minimo;
        const newPrice = isUsingMinPrice ? product.precio_venta : product.precio_minimo;
        
        return {
          ...item,
          precio_unitario: newPrice,
          subtotal: newPrice * item.cantidad,
          usando_precio_minimo: !isUsingMinPrice
        };
      }
      return item;
    }));
  };

  const addToCart = async (product) => {
    const existingItem = cart.find((item) => item.producto_id === product.id);
    
    // Obtener precio según lista del cliente
    let precio = product.precio_venta;
    try {
      const response = await axios.get(
        `${API}/products/${product.id}/price?cliente_id=${formData.cliente_id || ''}`
      );
      precio = response.data.precio_final;
    } catch (error) {
      console.error('Error al obtener precio:', error);
      // Si falla, usar precio base
      precio = product.precio_venta;
    }
    
    if (existingItem) {
      if (existingItem.cantidad >= product.existencia) {
        toast.error('No hay suficiente stock');
        return;
      }
      setCart(
        cart.map((item) =>
          item.producto_id === product.id
            ? { ...item, cantidad: item.cantidad + 1, subtotal: (item.cantidad + 1) * item.precio_unitario }
            : item
        )
      );
    } else {
      setCart([
        ...cart,
        {
          producto_id: product.id,
          producto_nombre: product.nombre,
          cantidad: 1,
          precio_unitario: precio,
          subtotal: precio,
          usando_precio_minimo: false,
        },
      ]);
    }
  };

  const updateCartQuantity = (productId, newQuantity) => {
    const product = products.find(p => p.id === productId);
    if (newQuantity > product.existencia) {
      toast.error('No hay suficiente stock');
      return;
    }
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(
      cart.map((item) =>
        item.producto_id === productId
          ? { ...item, cantidad: newQuantity, subtotal: newQuantity * item.precio_unitario }
          : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter((item) => item.producto_id !== productId));
  };

  const calculateTotals = () => {
    // El precio de venta YA INCLUYE el ITBIS
    const totalConItbis = cart.reduce((sum, item) => sum + item.subtotal, 0);
    
    if (businessConfig?.itbis_habilitado) {
      // Calcular el subtotal SIN ITBIS (precio base)
      // Formula: subtotal = total / (1 + tasa_itbis)
      const tasaItbis = businessConfig.itbis_porcentaje / 100;
      const subtotal = totalConItbis / (1 + tasaItbis);
      const itbis = totalConItbis - subtotal;
      
      return { 
        subtotal: Math.round(subtotal * 100) / 100, 
        itbis: Math.round(itbis * 100) / 100, 
        total: totalConItbis 
      };
    } else {
      // Si ITBIS no está habilitado, el precio es el subtotal
      return { 
        subtotal: totalConItbis, 
        itbis: 0, 
        total: totalConItbis 
      };
    }
  };

  const getErrorMessage = (error) => {
    // Handle Pydantic validation errors (array of objects)
    if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
      return error.response.data.detail.map(err => err.msg).join(', ');
    }
    
    // Handle simple string errors
    if (typeof error.response?.data?.detail === 'string') {
      return error.response.data.detail;
    }
    
    // Handle object errors
    if (typeof error.response?.data?.detail === 'object') {
      return JSON.stringify(error.response.data.detail);
    }
    
    // Default error message
    return 'Error al procesar la solicitud';
  };

  const handleCreateQuotation = async () => {
    if (cart.length === 0) {
      toast.error('Agregue productos a la cotización');
      return;
    }

    if (!formData.cliente_nombre.trim()) {
      toast.error('Ingrese el nombre del cliente');
      return;
    }

    try {
      const quotationData = {
        cliente: {
          nombre: formData.cliente_nombre,
          rnc: formData.cliente_rnc,
          telefono: formData.cliente_telefono,
          direccion: formData.cliente_direccion,
        },
        items: cart,
        observaciones: formData.observaciones,
        validez_dias: formData.validez_dias,
      };

      // Determinar el cliente_id a usar:
      // - Si hay cliente_id en formData, usar ese (cliente registrado seleccionado)
      // - Si no hay cliente_id pero sí consumidorFinalId, usar el del Consumidor Final (cliente casual con datos editados)
      if (formData.cliente_id) {
        quotationData.cliente_id = formData.cliente_id;
      } else if (consumidorFinalId) {
        // Cliente casual: usar ID del Consumidor Final pero con nombre/RNC personalizados
        quotationData.cliente_id = consumidorFinalId;
      }

      const response = await axios.post(`${API}/quotations`, quotationData);
      const createdQuotation = response.data;
      
      toast.success('Cotización creada exitosamente');
      setDialogOpen(false);
      resetForm();
      fetchQuotations();
      
      // Automatically print quotation
      setTimeout(() => {
        handlePrintQuotation(createdQuotation);
      }, 500);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      console.error('Error completo:', error.response?.data);
    }
  };

  const handleCancelQuotation = async (invoiceId) => {
    if (window.confirm('¿Está seguro de cancelar esta cotización?')) {
      try {
        await axios.put(`${API}/quotations/${invoiceId}/cancel`);
        toast.success('Cotización cancelada exitosamente');
        fetchQuotations();
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        toast.error(errorMessage);
      }
    }
  };

  const resetForm = () => {
    // Buscar Consumidor Final para cargar sus datos por defecto
    const consumidorFinal = customers.find(c => c.cedula === "000-0000000-0");
    
    if (consumidorFinal) {
      setConsumidorFinalId(consumidorFinal.id);
      setFormData({
        cliente_id: '', // Vacío para permitir edición
        cliente_nombre: consumidorFinal.nombre, // PRELLENADO con "CONSUMIDOR FINAL" pero editable
        cliente_rnc: consumidorFinal.rnc || '', // Prellenado con RNC por defecto
        cliente_telefono: consumidorFinal.telefono || '',
        cliente_direccion: consumidorFinal.direccion || '',
        vendedor: user?.full_name || '',
        fecha: getLocalDateString(),
        observaciones: '',
        validez_dias: 30,
      });
      // Cargar lista de precios General
      loadCustomerPriceList(consumidorFinal);
    } else {
      // Fallback si no se encuentra Consumidor Final
      setFormData({
        cliente_id: '',
        cliente_nombre: 'CONSUMIDOR FINAL', // Prellenado
        cliente_rnc: '000000000', // Prellenado
        cliente_telefono: '',
        cliente_direccion: '',
        vendedor: user?.full_name || '',
        fecha: getLocalDateString(),
        observaciones: '',
        validez_dias: 30,
      });
      setCustomerPriceList(null);
    }
    
    setSelectedCustomer(null);
    setCart([]);
    setCombinedPayments({ efectivo: 0, tarjeta: 0, transferencia: 0, cheque: 0 });
    setProductCodeInput('');
    setProductSearchTerm('');
  };

  const viewQuotationDetail = (invoice) => {
    setSelectedQuotation(invoice);
    setDetailDialogOpen(true);
  };

  const filteredQuotations = quotations.filter(
    (inv) =>
      inv.numero_cotizacion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.cliente?.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inv.cliente?.rnc?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quotations-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-slate-800">Cotizaciones</h1>
          <p className="text-slate-600 mt-1">Gestiona las cotizacións y ventas</p>
        </div>

        {canCreate && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="create-invoice-btn">
                <Receipt className="mr-2" size={18} />
                Nueva Cotización
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-7xl max-h-[95vh] overflow-y-auto" data-testid="create-invoice-dialog">
              <DialogHeader>
                <DialogTitle className="text-2xl">Crear Nueva Cotización</DialogTitle>
                <DialogDescription>Módulo de Cotizaciónción Tradicional</DialogDescription>
              </DialogHeader>
              
              {/* ENCABEZADO DE FACTURA */}
              <div className="bg-slate-50 p-4 rounded-lg space-y-4 border border-slate-200">
                <h3 className="font-semibold text-lg text-slate-800 border-b pb-2">Encabezado de Cotización</h3>
                
                <div className="grid grid-cols-4 gap-4">
                  {/* Búsqueda rápida de cliente */}
                  <div className="col-span-2">
                    <Label htmlFor="customer-quick-search">Buscar Cliente (RNC/Cédula/Teléfono/Nombre)</Label>
                    <div className="flex gap-2">
                      <Input
                        id="customer-quick-search"
                        value={customerSearchTerm}
                        onChange={handleCustomerSearch}
                        onKeyDown={handleCustomerSearchKeyDown}
                        placeholder="Escriba para buscar automáticamente o presione ENTER..."
                        className="flex-1"
                      />
                      <Button 
                        type="button" 
                        onClick={() => setCustomerSearchOpen(true)}
                        variant="outline"
                        className="whitespace-nowrap"
                      >
                        <Search size={16} className="mr-1" />
                        Buscar
                      </Button>
                    </div>
                  </div>

                  {/* Fecha */}
                  <div>
                    <Label htmlFor="fecha">Fecha *</Label>
                    <Input
                      id="fecha"
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                      required
                    />
                  </div>

                  {/* Vendedor */}
                  <div>
                    <Label htmlFor="vendedor">Vendedor</Label>
                    <Input
                      id="vendedor"
                      value={formData.vendedor}
                      readOnly
                      className="bg-slate-100"
                    />
                  </div>
                </div>

                {/* Cliente seleccionado */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cliente_nombre">Cliente Seleccionado *</Label>
                    <Input
                      id="cliente_nombre"
                      value={formData.cliente_nombre}
                      onChange={(e) => setFormData({ ...formData, cliente_nombre: e.target.value })}
                      readOnly={formData.cliente_id !== ''}
                      disabled={formData.cliente_id !== ''}
                      className={formData.cliente_id === '' ? "bg-white font-semibold" : "bg-slate-50 font-semibold"}
                      placeholder={formData.cliente_id === '' ? "Digite el nombre del cliente..." : ""}
                    />
                    {customerPriceList && (
                      <div className="mt-1 flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded">
                          💰 Lista: {customerPriceList.nombre}
                        </span>
                        {customerPriceList.descuento_global > 0 && (
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-semibold rounded">
                            -{customerPriceList.descuento_global}% Global
                          </span>
                        )}
                      </div>
                    )}
                    {!customerPriceList && formData.cliente_nombre && (
                      <div className="mt-1">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-semibold rounded">
                          💰 Lista: General (Por defecto)
                        </span>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="cliente_rnc">RNC/Cédula</Label>
                    <Input
                      id="cliente_rnc"
                      value={formData.cliente_rnc}
                      onChange={(e) => setFormData({ ...formData, cliente_rnc: e.target.value })}
                      readOnly={formData.cliente_id !== ''}
                      disabled={formData.cliente_id !== ''}
                      className={formData.cliente_id === '' ? "bg-white" : "bg-slate-50"}
                      placeholder={formData.cliente_id === '' ? "Digite el RNC/Cédula..." : ""}
                    />
                  </div>
                </div>

                {/* Campos ocultos con valores por defecto */}
                <div className="grid grid-cols-2 gap-4">
                  <input type="hidden" value="30" />
                  <input type="hidden" value="efectivo" />

                  {formData.es_credito && selectedCustomer && (
                    <div>
                      <Label>Crédito Disponible</Label>
                      <div className="flex items-center gap-2 p-2 bg-blue-50 rounded border border-blue-200">
                        <span className="text-sm font-semibold text-blue-800">
                          {formatCurrency(selectedCustomer.credito_disponible || 0)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Validación de crédito */}
                {formData.es_credito && selectedCustomer && (
                  <div className={`p-3 rounded-lg border ${
                    (selectedCustomer.credito_disponible || 0) < calculateTotals().total 
                      ? 'bg-red-50 border-red-300' 
                      : 'bg-green-50 border-green-300'
                  }`}>
                    <div className="flex items-center gap-2">
                      {(selectedCustomer.credito_disponible || 0) < calculateTotals().total ? (
                        <>
                          <AlertTriangle size={20} className="text-red-600" />
                          <div className="text-sm text-red-800">
                            <strong>Crédito Insuficiente:</strong> El cliente no tiene crédito disponible suficiente.
                            <div className="mt-1">
                              Límite: {formatCurrency(selectedCustomer.limite_credito || 0)} | 
                              Disponible: {formatCurrency(selectedCustomer.credito_disponible || 0)} | 
                              Total cotización: {formatCurrency(calculateTotals().total)}
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          <CheckCircle2 size={20} className="text-green-600" />
                          <div className="text-sm text-green-800">
                            <strong>Crédito Disponible</strong>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* SELECCIÓN DE PRODUCTOS */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg text-slate-800 border-b pb-2">Selección de Productos</h3>
                
                {/* Campo de código y atajos */}
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label htmlFor="product-code-input">Código Interno / Código de Barras</Label>
                    <form onSubmit={handleProductCodeSubmit} className="flex gap-2">
                      <Input
                        id="product-code-input"
                        value={productCodeInput}
                        onChange={(e) => setProductCodeInput(e.target.value)}
                        placeholder="Escriba o escanee el código..."
                        className="flex-1"
                      />
                      <Button type="submit" variant="outline">
                        Agregar
                      </Button>
                    </form>
                  </div>
                  <Button 
                    type="button" 
                    onClick={() => setProductSearchOpen(true)}
                    className="bg-blue-500 hover:bg-blue-600"
                  >
                    <Search size={16} className="mr-2" />
                    Búsqueda Avanzada (F2)
                  </Button>
                </div>

                <div className="text-xs text-slate-500 flex gap-4">
                  <span>💡 <kbd className="px-2 py-1 bg-slate-100 rounded">F2</kbd> Búsqueda avanzada</span>
                  <span>💡 <kbd className="px-2 py-1 bg-slate-100 rounded">F3</kbd> Focus en código</span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-6">
                {/* TABLA DE PRODUCTOS */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg text-slate-800 flex items-center gap-2">
                    <ShoppingCart size={20} />
                    Productos en Cotización ({cart.length})
                  </h3>
                  
                  {cart.length === 0 ? (
                    <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-300">
                      <ShoppingCart size={48} className="mx-auto text-slate-400 mb-3" />
                      <p className="text-slate-600">No hay productos agregados</p>
                      <p className="text-sm text-slate-500 mt-1">Use el campo de código o búsqueda avanzada (F2)</p>
                    </div>
                  ) : (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-100 border-b">
                          <tr>
                            <th className="text-left p-2 font-semibold">Cód. Interno</th>
                            <th className="text-left p-2 font-semibold">Producto</th>
                            <th className="text-center p-2 font-semibold w-24">Cantidad</th>
                            <th className="text-right p-2 font-semibold">Precio</th>
                            <th className="text-center p-2 font-semibold w-28">Tipo Precio</th>
                            <th className="text-right p-2 font-semibold">Importe</th>
                            <th className="text-center p-2 font-semibold">Stock</th>
                            <th className="text-center p-2 font-semibold w-20">Acción</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cart.map((item, index) => {
                            const subtotalLinea = item.precio_unitario * item.cantidad;
                            const producto = products.find(p => p.id === item.producto_id);
                            const stockBajo = producto && producto.existencia < 10;
                            
                            return (
                              <tr key={item.producto_id} className="border-b hover:bg-slate-50">
                                <td className="p-2 text-slate-700">{producto?.codigo_interno || '-'}</td>
                                <td className="p-2">
                                  <div className="font-medium text-slate-800">{item.producto_nombre}</div>
                                  <div className="text-xs text-slate-500">{producto?.unidad_medida}</div>
                                </td>
                                <td className="p-2">
                                  <div className="flex items-center justify-center gap-1">
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={() => updateCartQuantity(item.producto_id, item.cantidad - 1)}
                                      className="h-7 w-7 p-0"
                                    >
                                      <Minus size={14} />
                                    </Button>
                                    <Input
                                      type="number"
                                      min="1"
                                      value={item.cantidad}
                                      onChange={(e) => updateCartQuantity(item.producto_id, parseInt(e.target.value) || 1)}
                                      className="w-16 h-7 text-center"
                                    />
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={() => updateCartQuantity(item.producto_id, item.cantidad + 1)}
                                      className="h-7 w-7 p-0"
                                    >
                                      <Plus size={14} />
                                    </Button>
                                  </div>
                                </td>
                                <td className="p-2 text-right font-medium">{formatCurrency(item.precio_unitario)}</td>
                                <td className="p-2 text-center">
                                  {(!customerPriceList || customerPriceList.es_general) && producto?.precio_minimo ? (
                                    <div className="flex flex-col gap-1">
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant={item.usando_precio_minimo ? "outline" : "default"}
                                        onClick={() => togglePriceType(item.producto_id)}
                                        className={`h-6 text-xs px-2 ${
                                          item.usando_precio_minimo ? 'bg-white' : 'bg-emerald-500 hover:bg-emerald-600'
                                        }`}
                                        title="Usar precio normal"
                                      >
                                        Normal
                                      </Button>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant={item.usando_precio_minimo ? "default" : "outline"}
                                        onClick={() => togglePriceType(item.producto_id)}
                                        className={`h-6 text-xs px-2 ${
                                          item.usando_precio_minimo ? 'bg-blue-500 hover:bg-blue-600' : 'bg-white'
                                        }`}
                                        title={`Usar precio mínimo (${formatCurrency(producto.precio_minimo)})`}
                                      >
                                        Mínimo
                                      </Button>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-slate-400">-</span>
                                  )}
                                </td>
                                <td className="p-2 text-right font-bold text-green-600">{formatCurrency(subtotalLinea)}</td>
                                <td className="p-2 text-center">
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                    stockBajo ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                  }`}>
                                    {producto?.existencia || 0}
                                  </span>
                                </td>
                                <td className="p-2 text-center">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => removeFromCart(item.producto_id)}
                                    className="h-7 w-7 p-0 hover:bg-red-100 hover:text-red-600"
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* OBSERVACIONES */}
                <div className="space-y-2">
                  <Label htmlFor="observaciones">Observaciones / Notas</Label>
                  <Textarea
                    id="observaciones"
                    value={formData.observaciones || ''}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setFormData({ ...formData, observaciones: e.target.value });
                      }
                    }}
                    rows={3}
                    placeholder="Notas internas, comentarios del cliente, instrucciones especiales..."
                    className="resize-none"
                  />
                  <div className="flex justify-between text-xs text-slate-500">
                    <span>Máximo 500 caracteres</span>
                    <span className={(formData.observaciones || '').length > 450 ? 'text-red-600 font-semibold' : ''}>
                      {(formData.observaciones || '').length} / 500
                    </span>
                  </div>
                </div>

                {/* TOTALES */}
                <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-6 rounded-lg border-2 border-slate-300">
                  <h3 className="font-semibold text-lg text-slate-800 mb-4 border-b pb-2">Resumen de Cotización</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-base">
                      <span className="text-slate-700">Subtotal:</span>
                      <span className="font-semibold text-slate-900">{formatCurrency(totals.subtotal)}</span>
                    </div>
                    
                    {businessConfig?.itbis_habilitado && (
                      <div className="flex justify-between items-center text-base">
                        <span className="text-slate-700">ITBIS ({businessConfig.itbis_porcentaje}%):</span>
                        <span className="font-semibold text-slate-900">{formatCurrency(totals.itbis)}</span>
                      </div>
                    )}
                    
                    <div className="border-t-2 border-slate-400 pt-3 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="text-xl font-bold text-slate-900">TOTAL:</span>
                        <span className="text-3xl font-bold text-green-600">{formatCurrency(totals.total)}</span>
                      </div>
                    </div>
                    
                    {formData.tipo_pago === 'combinado' && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                        <div className="text-sm font-semibold text-blue-800 mb-2">Desglose Pago Combinado:</div>
                        <div className="space-y-1 text-sm">
                          {combinedPayments.efectivo > 0 && (
                            <div className="flex justify-between">
                              <span>Efectivo:</span>
                              <span className="font-semibold">{formatCurrency(combinedPayments.efectivo)}</span>
                            </div>
                          )}
                          {combinedPayments.tarjeta > 0 && (
                            <div className="flex justify-between">
                              <span>Tarjeta:</span>
                              <span className="font-semibold">{formatCurrency(combinedPayments.tarjeta)}</span>
                            </div>
                          )}
                          {combinedPayments.transferencia > 0 && (
                            <div className="flex justify-between">
                              <span>Transferencia:</span>
                              <span className="font-semibold">{formatCurrency(combinedPayments.transferencia)}</span>
                            </div>
                          )}
                          {combinedPayments.cheque > 0 && (
                            <div className="flex justify-between">
                              <span>Cheque:</span>
                              <span className="font-semibold">{formatCurrency(combinedPayments.cheque)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Botón de Crear Cotización */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => {
                      setDialogOpen(false);
                      resetForm();
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleCreateQuotation}
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-lg py-6"
                    disabled={
                      cart.length === 0 || 
                      !formData.cliente_nombre.trim() ||
                      (formData.es_credito && selectedCustomer && (selectedCustomer.credito_disponible || 0) < calculateTotals().total)
                    }
                    data-testid="submit-invoice-btn-main"
                  >
                    <Receipt className="mr-2" size={20} />
                    Crear Cotización {cart.length > 0 && `(${formatCurrency(totals.total)})`}
                  </Button>
                </div>

                {/* Cart and Form (Legacy - keeping for compatibility) */}
                <div className="space-y-4 hidden">
                  <div className="space-y-3">
                    <Label htmlFor="tipo_ncf">Tipo Cotización</Label>
                    <Select value={formData.tipo_ncf} onValueChange={(value) => setFormData({ ...formData, tipo_ncf: value })}>
                      <SelectTrigger data-testid="form-tipo-ncf-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="B01">B01 - Crédito Fiscal</SelectItem>
                        <SelectItem value="B02">B02 - Consumidor Final</SelectItem>
                      </SelectContent>
                    </Select>

                    <Label htmlFor="customer_select">Seleccionar Cliente Registrado (Opcional)</Label>
                    <Select value={formData.cliente_id || "new"} onValueChange={handleCustomerSelect}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar cliente..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">Cliente nuevo / Manual</SelectItem>
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.nombre} {customer.tiene_credito && `(Crédito: ${formatCurrency(customer.credito_disponible)})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {selectedCustomer && selectedCustomer.tiene_credito && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
                        <div className="font-semibold text-emerald-800">Cliente con Crédito</div>
                        <div className="text-emerald-700 mt-1">
                          Límite: {formatCurrency(selectedCustomer.limite_credito)}
                        </div>
                        <div className="text-emerald-700">
                          Disponible: {formatCurrency(selectedCustomer.credito_disponible)}
                        </div>
                      </div>
                    )}

                    <Label htmlFor="cliente_nombre">Cliente *</Label>
                    <Input
                      id="cliente_nombre"
                      value={formData.cliente_nombre}
                      onChange={(e) => setFormData({ ...formData, cliente_nombre: e.target.value })}
                      placeholder="Nombre del cliente"
                      data-testid="form-cliente-nombre-input"
                      disabled={!!formData.cliente_id}
                    />

                    <Label htmlFor="cliente_rnc">RNC</Label>
                    <Input
                      id="cliente_rnc"
                      value={formData.cliente_rnc}
                      onChange={(e) => setFormData({ ...formData, cliente_rnc: e.target.value })}
                      placeholder="RNC (opcional)"
                      data-testid="form-cliente-rnc-input"
                      disabled={!!formData.cliente_id}
                    />

                    {/* Validez y Forma de Pago ocultos con valores por defecto */}
                    <input type="hidden" id="validez_dias" value="30" />
                    <input type="hidden" id="tipo_pago" value="efectivo" />

                    <div className="flex items-center space-x-2 pt-2" style={{display: 'none'}}>
                      <input
                        type="checkbox"
                        id="es_credito_hidden"
                        checked={false}
                        onChange={(e) => {
                          const isCredito = e.target.checked;
                          setFormData({ 
                            ...formData, 
                            es_credito: isCredito,
                            tipo_pago: isCredito ? 'credito' : 'efectivo'
                          });
                        }}
                        className="w-4 h-4"
                        data-testid="form-es-credito-checkbox"
                      />
                      <Label htmlFor="es_credito" className="cursor-pointer">Venta a Crédito</Label>
                    </div>

                    {formData.es_credito && (
                      <>
                        <Label htmlFor="dias_credito">Días de Crédito</Label>
                        <Input
                          id="dias_credito"
                          type="number"
                          value={formData.dias_credito}
                          onChange={(e) => setFormData({ ...formData, dias_credito: parseInt(e.target.value) })}
                          min="1"
                          data-testid="form-dias-credito-input"
                        />
                      </>
                    )}
                  </div>

                  {/* Cart Items */}
                  <div className="border-t pt-4">
                    <h4 className="font-semibold mb-3">Carrito ({cart.length})</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {cart.map((item) => (
                        <div key={item.producto_id} className="flex items-center justify-between text-sm bg-slate-50 p-2 rounded" data-testid={`cart-item-${item.producto_id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate text-xs">{item.producto_nombre}</div>
                            <div className="text-xs text-slate-600">{formatCurrency(item.precio_unitario)}</div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Input
                              type="number"
                              min="1"
                              value={item.cantidad}
                              onChange={(e) => updateCartQuantity(item.producto_id, parseInt(e.target.value))}
                              className="w-14 h-7 text-xs"
                              data-testid={`cart-quantity-${item.producto_id}`}
                            />
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeFromCart(item.producto_id)}
                              className="h-7 w-7 p-0"
                              data-testid={`cart-remove-${item.producto_id}`}
                            >
                              <X size={14} />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(totals.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>ITBIS (18%):</span>
                      <span>{formatCurrency(totals.itbis)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Total:</span>
                      <span className="text-green-600">{formatCurrency(totals.total)}</span>
                    </div>
                  </div>

                  <Button
                    onClick={handleCreateQuotation}
                    className="w-full bg-emerald-500 hover:bg-emerald-600"
                    disabled={cart.length === 0}
                    data-testid="submit-invoice-btn"
                  >
                    Crear Cotización
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filtros de Rango de Fecha */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Fecha Desde */}
          <div>
            <Label htmlFor="fecha-desde" className="text-sm font-medium text-slate-700 mb-1 block">
              Fecha Desde
            </Label>
            <Input
              id="fecha-desde"
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              className="w-full"
            />
          </div>

          {/* Fecha Hasta */}
          <div>
            <Label htmlFor="fecha-hasta" className="text-sm font-medium text-slate-700 mb-1 block">
              Fecha Hasta
            </Label>
            <Input
              id="fecha-hasta"
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              className="w-full"
            />
          </div>
        </div>
      </div>

      {/* Búsqueda en Tiempo Real */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
        <Input
          placeholder="Buscar cotizacións por número, cliente, Cotización..."
          className="pl-10"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-testid="search-quotations-input"
        />
      </div>

      {/* Quotations Table */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="custom-table">
            <thead>
              <tr>
                <th>No. Cotización</th>
                <th>NO. FACTURA</th>
                <th>Código</th>
                <th>Cliente</th>
                <th>Fecha</th>
                <th>Total</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody data-testid="quotations-table-body">
              {filteredQuotations.map((invoice) => (
                <tr key={invoice.id} data-testid={`invoice-row-${invoice.numero_cotizacion}`}>
                  <td className="font-medium">{invoice.numero_cotizacion}</td>
                  <td className="text-sm">
                    {invoice.status === 'convertida' && invoice.factura_numero ? (
                      <span className="font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                        {invoice.factura_numero}
                      </span>
                    ) : (
                      <span className="text-slate-400">-</span>
                    )}
                  </td>
                  <td className="font-mono text-blue-600 font-medium">{getClientCode(invoice.cliente?.nombre)}</td>
                  <td>
                    <div className="font-medium">{invoice.cliente.nombre}</div>
                    {invoice.cliente.rnc && <div className="text-xs text-slate-500">RNC: {invoice.cliente.rnc}</div>}
                  </td>
                  <td className="text-sm">{invoice.fecha}</td>
                  <td className="font-semibold text-green-600">{formatCurrency(invoice.total)}</td>
                  <td>
                    <span
                      className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                        invoice.status === 'pendiente'
                          ? 'bg-yellow-100 text-yellow-700'
                          : invoice.status === 'convertida'
                          ? 'bg-green-100 text-green-700'
                          : invoice.status === 'vencida'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {invoice.status === 'pendiente' 
                        ? 'Pendiente' 
                        : invoice.status === 'convertida' 
                        ? 'Facturada' 
                        : invoice.status === 'vencida'
                        ? 'Vencida'
                        : 'Cancelada'}
                    </span>
                  </td>
                  <td>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewQuotationDetail(invoice)}
                        data-testid={`view-invoice-btn-${invoice.numero_cotizacion}`}
                        title="Ver detalles"
                      >
                        <Eye size={16} />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`print-invoice-btn-${invoice.numero_cotizacion}`}
                            title="Imprimir cotización"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Printer size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => handlePrintQuotation(invoice)}>
                            <Printer size={16} className="mr-2" />
                            Formato POS (80mm)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => printQuotationA4(invoice)}>
                            <FileText size={16} className="mr-2" />
                            Formato A4
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => downloadQuotationPDF(invoice)}>
                            <Download size={16} className="mr-2" />
                            Descargar PDF
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      {canCancel && (invoice.status === 'pendiente' || invoice.status === 'vencida') && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleCancelQuotation(invoice.id)}
                          data-testid={`cancel-invoice-btn-${invoice.numero_cotizacion}`}
                          title="Cancelar cotización"
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredQuotations.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            No se encontraron cotizaciones
          </div>
        )}
      </div>

      {/* Quotation Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="invoice-detail-dialog">
          {selectedQuotation && (
            <>
              <DialogHeader>
                <DialogTitle>Detalle de Cotización #{selectedQuotation.numero_cotizacion}</DialogTitle>
                <DialogDescription>Cotización: {selectedQuotation.ncf}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-semibold">Cliente:</span> {selectedQuotation.cliente.nombre}
                  </div>
                  <div>
                    <span className="font-semibold">Fecha:</span>{' '}
                    {selectedQuotation.fecha}
                  </div>
                  {selectedQuotation.cliente.rnc && (
                    <div>
                      <span className="font-semibold">RNC:</span> {selectedQuotation.cliente.rnc}
                    </div>
                  )}
                  <div>
                    <span className="font-semibold">Vendedor:</span> {selectedQuotation.vendedor_nombre}
                  </div>
                  <div>
                    <span className="font-semibold">Forma de Pago:</span>{' '}
                    <span className="capitalize">{selectedQuotation.tipo_pago}</span>
                  </div>
                  <div>
                    <span className="font-semibold">Estado:</span>{' '}
                    <span className={
                      selectedQuotation.status === 'pendiente' 
                        ? 'text-yellow-600 font-semibold' 
                        : selectedQuotation.status === 'convertida'
                        ? 'text-green-600 font-semibold'
                        : selectedQuotation.status === 'vencida'
                        ? 'text-orange-600 font-semibold'
                        : 'text-red-600 font-semibold'
                    }>
                      {selectedQuotation.status === 'pendiente' 
                        ? '⏳ Pendiente' 
                        : selectedQuotation.status === 'convertida' 
                        ? '✅ Facturada' 
                        : selectedQuotation.status === 'vencida'
                        ? '⚠️ Vencida'
                        : '❌ Cancelada'}
                    </span>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Items</h4>
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left p-2">Producto</th>
                        <th className="text-right p-2">Cant.</th>
                        <th className="text-right p-2">Precio</th>
                        <th className="text-right p-2">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedQuotation.items.map((item, index) => (
                        <tr key={index} className="border-b">
                          <td className="p-2">{item.producto_nombre}</td>
                          <td className="text-right p-2">{item.cantidad}</td>
                          <td className="text-right p-2">{formatCurrency(item.precio_unitario)}</td>
                          <td className="text-right p-2 font-semibold">{formatCurrency(item.subtotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>{formatCurrency(selectedQuotation.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>ITBIS (18%):</span>
                    <span>{formatCurrency(selectedQuotation.itbis)}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total:</span>
                    <span className="text-green-600">{formatCurrency(selectedQuotation.total)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <Button
                    onClick={() => handlePrintQuotation(selectedQuotation)}
                    className="bg-blue-500 hover:bg-blue-600"
                    data-testid="print-invoice-btn"
                  >
                    <Printer size={16} className="mr-2" />
                    POS
                  </Button>
                  <Button
                    onClick={() => printQuotationA4(selectedQuotation)}
                    variant="outline"
                    className="border-blue-500 text-blue-500 hover:bg-blue-50"
                  >
                    <FileText size={16} className="mr-2" />
                    A4
                  </Button>
                  <Button
                    onClick={() => downloadQuotationPDF(selectedQuotation)}
                    variant="outline"
                    className="border-green-500 text-green-500 hover:bg-green-50"
                  >
                    <Download size={16} className="mr-2" />
                    PDF
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Búsqueda de Clientes */}
      <Dialog open={customerSearchOpen} onOpenChange={setCustomerSearchOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Búsqueda Avanzada de Clientes</DialogTitle>
            <DialogDescription>Busque y seleccione un cliente registrado</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Buscar por nombre, RNC, cédula o teléfono..."
                value={customerSearchTerm}
                onChange={(e) => setCustomerSearchTerm(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>

            <div className="border rounded-lg max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Nombre</th>
                    <th className="text-left p-2">RNC/Cédula</th>
                    <th className="text-left p-2">Teléfono</th>
                    <th className="text-right p-2">Crédito Disp.</th>
                    <th className="text-center p-2">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {customers
                    .filter(c => {
                      const search = customerSearchTerm.toLowerCase();
                      return (
                        c.nombre.toLowerCase().includes(search) ||
                        c.rnc?.toLowerCase().includes(search) ||
                        c.cedula?.toLowerCase().includes(search) ||
                        c.telefono?.includes(search)
                      );
                    })
                    .map(customer => (
                      <tr key={customer.id} className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => selectCustomer(customer)}>
                        <td className="p-2 font-medium">{customer.nombre}</td>
                        <td className="p-2 text-slate-600">{customer.rnc || customer.cedula || '-'}</td>
                        <td className="p-2 text-slate-600">{customer.telefono || '-'}</td>
                        <td className="p-2 text-right">
                          {customer.tiene_credito ? (
                            <span className="text-green-600 font-semibold">
                              {formatCurrency(customer.credito_disponible || 0)}
                            </span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          <Button
                            size="sm"
                            onClick={() => selectCustomer(customer)}
                            className="bg-emerald-500 hover:bg-emerald-600"
                          >
                            Seleccionar
                          </Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {customers.filter(c => {
                const search = customerSearchTerm.toLowerCase();
                return (
                  c.nombre.toLowerCase().includes(search) ||
                  c.rnc?.toLowerCase().includes(search) ||
                  c.cedula?.toLowerCase().includes(search) ||
                  c.telefono?.includes(search)
                );
              }).length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No se encontraron clientes
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Búsqueda Avanzada de Productos */}
      <Dialog open={productSearchOpen} onOpenChange={setProductSearchOpen}>
        <DialogContent className="max-w-6xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>Búsqueda Avanzada de Productos</DialogTitle>
            <DialogDescription>Busque productos por nombre, código o categoría</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
              <Input
                placeholder="Buscar por nombre, código interno, código de barras o categoría..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>

            <div className="border rounded-lg max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Cód. Interno</th>
                    <th className="text-left p-2">Producto</th>
                    <th className="text-left p-2">Categoría</th>
                    <th className="text-right p-2">Precio</th>
                    <th className="text-center p-2">Stock</th>
                    <th className="text-center p-2">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProductsForSearch.map(product => {
                    const stockBajo = product.existencia < 10;
                    const enCarrito = cart.find(item => item.producto_id === product.id);
                    
                    return (
                      <tr key={product.id} className="border-b hover:bg-slate-50">
                        <td className="p-2 text-slate-700 font-mono text-xs">{product.codigo_interno || '-'}</td>
                        <td className="p-2">
                          <div className="font-medium text-slate-800">{product.nombre}</div>
                          <div className="text-xs text-slate-500">{product.unidad_medida}</div>
                        </td>
                        <td className="p-2 text-slate-600">{product.categoria || '-'}</td>
                        <td className="p-2 text-right font-semibold text-green-600">{formatCurrency(product.precio_venta)}</td>
                        <td className="p-2 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            stockBajo ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {product.existencia}
                          </span>
                        </td>
                        <td className="p-2 text-center">
                          <Button
                            size="sm"
                            onClick={() => {
                              addToCart(product);
                              setProductSearchOpen(false);
                              setProductSearchTerm('');
                            }}
                            disabled={product.existencia === 0}
                            className="bg-blue-500 hover:bg-blue-600"
                          >
                            {enCarrito ? `Agregar (${enCarrito.cantidad})` : 'Agregar'}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredProductsForSearch.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No se encontraron productos
                </div>
              )}
            </div>

            <div className="flex justify-between items-center text-sm text-slate-600 bg-slate-50 p-3 rounded">
              <span>💡 Haga clic en un producto para agregarlo al carrito</span>
              <span className="font-semibold">{filteredProductsForSearch.length} productos encontrados</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Pago Combinado */}
      <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configurar Pago Combinado</DialogTitle>
            <DialogDescription>Distribuya el total de la cotización entre diferentes métodos de pago</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-slate-100 p-4 rounded-lg text-center">
              <div className="text-sm text-slate-600 mb-1">Total a Pagar</div>
              <div className="text-2xl font-bold text-slate-900">{formatCurrency(calculateTotals().total)}</div>
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="combined-efectivo">💵 Efectivo</Label>
                <Input
                  id="combined-efectivo"
                  type="number"
                  min="0"
                  step="0.01"
                  value={combinedPayments.efectivo}
                  onChange={(e) => setCombinedPayments({ ...combinedPayments, efectivo: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label htmlFor="combined-tarjeta">💳 Tarjeta</Label>
                <Input
                  id="combined-tarjeta"
                  type="number"
                  min="0"
                  step="0.01"
                  value={combinedPayments.tarjeta}
                  onChange={(e) => setCombinedPayments({ ...combinedPayments, tarjeta: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label htmlFor="combined-transferencia">🏦 Transferencia</Label>
                <Input
                  id="combined-transferencia"
                  type="number"
                  min="0"
                  step="0.01"
                  value={combinedPayments.transferencia}
                  onChange={(e) => setCombinedPayments({ ...combinedPayments, transferencia: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label htmlFor="combined-cheque">📝 Cheque</Label>
                <Input
                  id="combined-cheque"
                  type="number"
                  min="0"
                  step="0.01"
                  value={combinedPayments.cheque}
                  onChange={(e) => setCombinedPayments({ ...combinedPayments, cheque: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-slate-600">Total Ingresado:</span>
                <span className={`font-bold ${
                  Math.abs(Object.values(combinedPayments).reduce((a, b) => a + parseFloat(b || 0), 0) - calculateTotals().total) < 0.01
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}>
                  {formatCurrency(Object.values(combinedPayments).reduce((a, b) => a + parseFloat(b || 0), 0))}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">Diferencia:</span>
                <span className={`font-bold ${
                  Math.abs(Object.values(combinedPayments).reduce((a, b) => a + parseFloat(b || 0), 0) - calculateTotals().total) < 0.01
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}>
                  {formatCurrency(Object.values(combinedPayments).reduce((a, b) => a + parseFloat(b || 0), 0) - calculateTotals().total)}
                </span>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setCombinedPayments({ efectivo: 0, tarjeta: 0, transferencia: 0, cheque: 0 });
                  setPaymentModalOpen(false);
                  setFormData({ ...formData, tipo_pago: 'efectivo' });
                }}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                onClick={handleCombinedPaymentSave}
              >
                Confirmar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Quotations;
